// Navbar Convokko: barra superior con título de la página, fecha y menú de usuario.
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LogoutIcon from "@mui/icons-material/Logout";
import SportsIcon from "@mui/icons-material/Sports";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { useMaterialUIController, setMiniSidenav } from "context";

const PAGE_TITLES = {
  "/dashboard": { title: "Resumen", subtitle: "Visión general de tu escuela" },
  "/equipos": { title: "Equipos", subtitle: "Gestiona los equipos de la escuela" },
  "/entrenadores": { title: "Entrenadores", subtitle: "Cuerpo técnico de la escuela" },
  "/jugadores": { title: "Jugadores", subtitle: "Plantillas y fichas de jugadores" },
  "/entrenamientos": { title: "Entrenamientos", subtitle: "Sesiones y asistencia" },
  "/partidos": { title: "Partidos", subtitle: "Calendario de partidos y convocatorias" },
  "/calendario": { title: "Calendario", subtitle: "Agenda de eventos de la escuela" },
  "/clasificacion": { title: "Clasificación", subtitle: "Posiciones en competición" },
  "/perfil": { title: "Mi perfil", subtitle: "Tus datos y preferencias" },
};

const ROL_LABELS = {
  coordinador: "Coordinador",
  entrenador: "Entrenador",
};

function DashboardNavbar({ absolute, light, isMini }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);

  const page = PAGE_TITLES[location.pathname] || { title: "", subtitle: "" };

  useEffect(() => {
    document.title = page.title ? `${page.title} · Convokko` : "Convokko";
  }, [page.title]);

  const fecha = useMemo(
    () =>
      new Date().toLocaleDateString("es-ES", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    []
  );

  const rol = useMemo(() => {
    try {
      const token = localStorage.getItem("token");
      return token ? jwtDecode(token).rol : null;
    } catch (e) {
      return null;
    }
  }, []);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: 12,
        zIndex: 1100,
        bgcolor: "rgba(255,255,255,0.82)",
        backdropFilter: "saturate(180%) blur(12px)",
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: "16px",
        boxShadow: "0 4px 16px -8px rgba(22,32,74,.12)",
      }}
    >
      <Toolbar sx={{ minHeight: "64px !important", px: { xs: 1.5, sm: 3 } }}>
        <IconButton
          size="small"
          onClick={handleMiniSidenav}
          sx={{ mr: 1.5, color: "text.secondary" }}
          aria-label="Alternar menú lateral"
        >
          {miniSidenav ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2} noWrap>
            {page.title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display={{ xs: "none", sm: "block" }}
          >
            {page.subtitle}
          </Typography>
        </Box>

        <Box flexGrow={1} />

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ display: { xs: "none", md: "block" }, textTransform: "capitalize" }}
          >
            {fecha}
          </Typography>

          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              borderRadius: "24px",
              border: "1px solid",
              borderColor: "grey.300",
              bgcolor: "#fff",
              pl: 0.5,
              pr: 1,
              py: 0.5,
              transition: "all .15s ease",
              "&:hover": { borderColor: "primary.main", boxShadow: "0 2px 8px rgba(0,0,0,.08)" },
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: "#F59E0B" }}>
              <SportsIcon sx={{ fontSize: 18, color: "#fff" }} />
            </Avatar>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              {ROL_LABELS[rol] || "Usuario"}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          </Box>
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: { borderRadius: 3, minWidth: 200, mt: 1, boxShadow: "0 12px 32px rgba(0,0,0,.12)" },
          }}
        >
          <Box px={2} py={1.25}>
            <Typography variant="subtitle2" fontWeight={700}>
              {ROL_LABELS[rol] || "Usuario"}
            </Typography>
            <Chip
              label="Sesión activa"
              size="small"
              color="success"
              sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700, mt: 0.5 }}
            />
          </Box>
          <Divider />
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              navigate("/perfil");
            }}
            sx={{ py: 1.25, fontWeight: 600, fontSize: "0.85rem" }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            Mi perfil
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              navigate("/logout");
            }}
            sx={{ py: 1.25, color: "error.main", fontWeight: 600, fontSize: "0.85rem" }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            Cerrar sesión
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
